import "./richComponentMessage.css";
import { Suspense, useEffect, useState } from "react";
import * as ConversationEntryUtil from "../helpers/conversationEntryUtil";
import { util } from "../helpers/common";
import { t, getLang } from "../awac/i18n";
import { localizeAgentText, localizeOptionLabel } from "../awac/agentTextL10n";
import { findUnit, unitFeatureKeys } from "../unitsCatalog";
import { getAwacTemplate } from "../awac/templateRegistry";

// How many units the in-chat summary details before deferring to the gallery.
const UNIT_SUMMARY_MAX = 6;

/**
 * Extract the unit records from a UnitCarousel envelope
 * (curation[0].personalizations[0].data — tolerates the legacy raw-field shape).
 * @param {object} envelope
 * @returns {Array<object>}
 */
function extractCarouselUnits(envelope) {
    try {
        const personalization = envelope && Array.isArray(envelope.curation)
            && envelope.curation[0] && envelope.curation[0].personalizations
            && envelope.curation[0].personalizations[0];
        return Array.isArray(personalization && personalization.data) ? personalization.data : [];
    } catch (e) {
        return [];
    }
}

/**
 * Question-topic detectors: deterministic keyword patterns (EN + FR) matched
 * against the visitor's OWN message. Each key maps to extra facts worth
 * surfacing when the visitor asked about that theme. Never LLM-authored.
 */
const QUESTION_TOPICS = [
    { key: "noise", re: /noise|quiet|bruit|calme|silen|sound/i },
    { key: "view", re: /view|vue|window|fen[eê]tre|scenery|overlook/i },
    { key: "orientation", re: /orientat|facing|exposure|expos[ée]|sun|light|lumi[èe]|soleil/i },
    { key: "privacy", re: /privacy|private|intimit|vis-[àa]-vis/i },
    { key: "bedroomSize", re: /queen|king|bed size|fit .{0,20}bed|big .{0,20}bed(room)?|dimension|grandeur|lit (queen|king|double)/i },
    { key: "living", re: /living ?room|salon|s[ée]jour/i },
    // Kitchen DIMENSIONS only for explicit size questions ("how big is the
    // kitchen?", "kitchen dimensions"). A counter-space / big-kitchen WISH is
    // covered by the Oversized-counter-space premium chip and the ✗ flags —
    // room measurements are not relevant to it.
    { key: "kitchen", re: /(kitchen|cuisine).{0,20}(dimensions?|size)|(dimensions?|size|taille|grandeur|mesures?).{0,20}(kitchen|cuisine)|how (big|large).{0,25}(kitchen|cuisine)/i },
    { key: "balcony", re: /balcon|balcony|outdoor|terras|patio|ext[ée]rieur/i },
    { key: "floor", re: /\bfloor\b|[ée]tage|storey|level|highest|top floor|plus haut/i },
    { key: "ensuite", re: /en-?\s?suite|attenante|connected .{0,20}(bedroom|master)/i },
    { key: "corner", re: /corner|\bcoin\b/i },
    { key: "price", re: /price|prix|budget|\$|month|mois|loyer|rent|cost|dollar/i },
];

function detectQuestionTopics(question) {
    const topics = new Set();
    if (typeof question !== "string" || !question) {
        return topics;
    }
    for (const { key, re } of QUESTION_TOPICS) {
        if (re.test(question)) {
            topics.add(key);
        }
    }
    return topics;
}

// Localized display names for the Apex CRITERIA_FIELDS whitelist — used to
// render the "missing" descriptors partial-match units carry in the envelope.
const CRITERIA_FIELD_LABELS = {
    en: {
        Price__c: "Price", Nb_of_Bedrooms__c: "Bedrooms", Nb_of_Bathrooms__c: "Bathrooms",
        Square_Footage_Indoor__c: "Interior sq ft", Square_Footage_Outdoor__c: "Outdoor sq ft",
        Floor__c: "Floor", Corner_Unit__c: "Corner unit", Has_Den__c: "Den",
        Bedroom_1_Ensuite_Bathroom__c: "Ensuite bathroom",
        // Both kitchen flags share the unified feature label — when a unit
        // misses both, the Set-dedup collapses them into one ✗ flag.
        Bigger_Kitchen_Island__c: "Oversized counter space",
        Bigger_Kitchen_Countertops_excl_Island__c: "Oversized counter space",
        Walk_In_Closet_Entrance__c: "Walk-in closet",
        Side_by_Side_Washer_Dryer__c: "Side-by-side washer-dryer",
        Backyard__c: "Backyard", Side_Yard__c: "Side yard", Road_Noise__c: "Road noise",
        Sun_Exposure__c: "Sun exposure", Visual_Privacy__c: "Visual privacy",
        Orientation__c: "Orientation", Proximity_to_Elevators__c: "Elevator proximity",
        Floor_Plan_Type__c: "Layout", Unit_Number__c: "Unit number",
        Office_Space: "Office space"
    },
    fr: {
        Price__c: "Prix", Nb_of_Bedrooms__c: "Chambres", Nb_of_Bathrooms__c: "Salles de bain",
        Square_Footage_Indoor__c: "pi² intérieurs", Square_Footage_Outdoor__c: "pi² extérieurs",
        Floor__c: "Étage", Corner_Unit__c: "Unité de coin", Has_Den__c: "Den",
        Bedroom_1_Ensuite_Bathroom__c: "Salle de bain attenante",
        Bigger_Kitchen_Island__c: "Grand espace de comptoir",
        Bigger_Kitchen_Countertops_excl_Island__c: "Grand espace de comptoir",
        Walk_In_Closet_Entrance__c: "Walk-in à l’entrée",
        Side_by_Side_Washer_Dryer__c: "Laveuse-sécheuse côte à côte",
        Backyard__c: "Cour arrière", Side_Yard__c: "Cour latérale", Road_Noise__c: "Bruit routier",
        Sun_Exposure__c: "Ensoleillement", Visual_Privacy__c: "Intimité visuelle",
        Orientation__c: "Orientation", Proximity_to_Elevators__c: "Proximité des ascenseurs",
        Floor_Plan_Type__c: "Plan", Unit_Number__c: "Numéro d’unité",
        Office_Space: "Espace bureau"
    }
};

/** Locale-formatted CAD amount for the missing-price flags ("$1,800" / "1 800 $"). */
function fmtCad(v) {
    try {
        return new Intl.NumberFormat(getLang() === "fr" ? "fr-CA" : "en-CA", {
            style: "currency", currency: "CAD", maximumFractionDigits: 0
        }).format(Number(v));
    } catch (e) {
        return `$${v}`;
    }
}

/**
 * Human-readable label for one "missing" descriptor a partial-match unit
 * carries ({k, v?, f?, op?} — see ShowUnitsAwacService.missedConstraints).
 * Returns null for anything unrecognized so it is silently skipped.
 */
function missingFeatureLabel(m) {
    if (!m || !m.k) return null;
    switch (m.k) {
        case "beds": return t(m.v === "1" ? "miss.bed" : "miss.beds", { v: m.v });
        case "type": return m.v || null;
        case "sqftMin": return t("miss.sqftMin", { v: m.v });
        case "sqftMax": return t("miss.sqftMax", { v: m.v });
        case "priceMax": return t("miss.priceMax", { v: fmtCad(m.v) });
        case "priceMin": return t("miss.priceMin", { v: fmtCad(m.v) });
        case "corner": return t("feat.corner");
        case "ensuite": return t("feat.ensuite");
        case "crit": {
            const labels = CRITERIA_FIELD_LABELS[getLang()] || CRITERIA_FIELD_LABELS.en;
            const label = labels[m.f] || m.f;
            if (m.v === "true") return label;                       // boolean asked-for
            if (m.v === "false") return t("miss.without", { v: label.toLowerCase() });
            const sym = { "!=": "≠", ">=": "≥", "<=": "≤", ">": ">", "<": "<" }[m.op];
            return sym ? `${label} ${sym} ${m.v}` : `${label} : ${m.v}`;
        }
        default: return null;
    }
}

/* Secondary lifestyle facts (noise, privacy, sun, view) are SELLING POINTS
   only: their bullets render when the value is genuinely positive; middling
   or negative values simply stay out of the bullets. (The ✗ match flags still
   disclose missed CRITERIA — this governs only the descriptive lines.) */
const POSITIVE_NOISE = /silent|quiet|low/i;
const POSITIVE_PRIVACY = /high privacy/i;   // High / Very High Privacy
const POSITIVE_SUN = /high sun/i;           // High / Very High / Extremely High
const NEGATIVE_VIEW = /street|rue\b|parking|stationnement|wall|mur\b|vis-.?-vis|expos|limited|limit[ée]|obstru/i;

/**
 * Human-readable label for one POSITIVE note descriptor ({k, v?, d?}) the Apex
 * ladder attaches (stretch / underMin / bedsMore / denSub / bedOffice).
 * Null for anything unknown.
 */
function noteLabel(n) {
    if (!n || !n.k) return null;
    switch (n.k) {
        case "stretch": return t("note.stretch", { v: n.v });
        // Below the stated price floor while matching everything else —
        // v = the stated floor, d = the exact monthly saving (both raw dollars).
        case "underMin": return t("note.underMin", { v: fmtCad(n.v), d: fmtCad(n.d) });
        case "bedsMore": return t("note.bedsMore");
        case "denSub": return t("note.denSub");
        case "bedOffice": return t("note.bedOffice");
        default: return null;
    }
}

/**
 * Feature bullets for one unit — built from the SAME envelope data the gallery
 * renders (never LLM-authored), enriched with snapshot facts. When the visitor's
 * question mentions specific themes (noise, view, dimensions, ...), the bullets
 * MODULATE: the core facts (beds, baths, interior sqft) always show, followed
 * by only the asked-about facts. With no detected themes, the default set shows.
 * Facts missing in the data are simply skipped — never invented; negative
 * secondary facts are simply omitted — never advertised.
 * @param {object} u - envelope unit record
 * @param {object|null} snap - matching snapshot unit (unitsCatalog.findUnit)
 * @param {string} [question] - the visitor message this reply answers
 * @returns {Array<string>}
 */
function unitFeatureBullets(u, snap, question) {
    const lang = getLang();
    const locale = lang === "fr" ? "fr-CA" : "en-CA";
    const beds = Number(u.bedrooms != null ? u.bedrooms : u.Bedrooms__c);
    const baths = Number(u.bathrooms != null ? u.bathrooms : u.Bathrooms__c);
    // Envelope value first; snapshot fills the gap when the org field is empty.
    const sqft = Number(u.sizeSqFt != null ? u.sizeSqFt : (u.SizeSqFt__c != null ? u.SizeSqFt__c : (snap && snap.sqft)));
    const sqftOut = Number(u.balconySqFt != null ? u.balconySqFt : (snap && snap.sqftOut));
    const price = Number(u.price != null ? u.price : u.PriceFrom__c);
    const hasDen = u.hasDen === true || (snap && snap.den === true);
    const floor = snap && snap.floor != null ? Number(snap.floor) : NaN;

    // Reusable fact builders (each returns [] when the data is missing).
    const facts = {
        beds: () => !Number.isNaN(beds) && beds > 0
            ? [`${beds} ${t(beds > 1 ? "units.beds" : "units.bed")}${hasDen ? ` ${t("units.den")}` : ""}`]
            : (hasDen ? [t("units.den")] : []),
        baths: () => !Number.isNaN(baths) && baths > 0
            ? [`${baths} ${t(baths > 1 ? "units.baths" : "units.bath")}`]
            : [],
        sqft: () => !Number.isNaN(sqft) && sqft > 0
            ? [`${sqft.toLocaleString(locale)} ${t("units.sqft")} ${t("units.interior")}`]
            : [],
        balcony: () => {
            const out = [];
            if (!Number.isNaN(sqftOut) && sqftOut > 0) {
                out.push(`${sqftOut.toLocaleString(locale)} ${t("units.sqftBalcony")}`);
            }
            if (snap && snap.balcony) {
                out.push(t("units.balconyDims", { v: snap.balcony }));
            }
            return out;
        },
        floor: () => !Number.isNaN(floor) && floor > 0 ? [t("units.floorN", { n: floor })] : [],
        orientation: () => {
            const out = [];
            if (snap && snap.orientation) {
                out.push(t("units.orientationBullet", { v: snap.orientation }));
            }
            if (snap && snap.sun && POSITIVE_SUN.test(snap.sun)) {
                out.push(t("units.sunBullet", { v: snap.sun }));
            }
            return out;
        },
        view: () => {
            const out = [];
            if (snap && snap.view && !NEGATIVE_VIEW.test(snap.view)) {
                out.push(t("units.viewBullet", { v: snap.view }));
            }
            return out.concat(facts.orientation());
        },
        noise: () => {
            const out = [];
            if (snap && snap.noise && POSITIVE_NOISE.test(snap.noise)) {
                out.push(t("units.noiseBullet", { v: snap.noise }));
            }
            // Higher floors are quieter — an upper floor answers a noise
            // question positively even when the noise rating itself is silent.
            if (!Number.isNaN(floor) && floor >= 3) {
                out.push(t("units.quietFloor", { n: floor }));
            }
            return out;
        },
        ensuite: () => (snap && snap.ensuite === true) ? [t("feat.ensuite")] : [],
        corner: () => (snap && snap.corner === true) ? [t("feat.corner")] : [],
        privacy: () => (snap && snap.privacy && POSITIVE_PRIVACY.test(snap.privacy))
            ? [t("units.privacyBullet", { v: snap.privacy })] : [],
        bedroomSize: () => (snap && snap.bed1Dims) ? [t("units.bed1Bullet", { v: snap.bed1Dims })] : [],
        living: () => (snap && snap.livingDims) ? [t("units.livingBullet", { v: snap.livingDims })] : [],
        kitchen: () => (snap && snap.kitchenDims) ? [t("units.kitchenBullet", { v: snap.kitchenDims })] : [],
        price: () => {
            if (Number.isNaN(price) || price <= 0) {
                return [];
            }
            try {
                return [new Intl.NumberFormat(locale, {
                    style: "currency", currency: "CAD", maximumFractionDigits: 0
                }).format(price) + t("unit.perMonth")];
            } catch (e) {
                return [`$${price.toLocaleString()}${t("unit.perMonth")}`];
            }
        },
    };

    const topics = detectQuestionTopics(question);
    let bullets;
    if (topics.size > 0) {
        // Core facts first, then only the themes the visitor asked about.
        bullets = [...facts.beds(), ...facts.baths(), ...facts.sqft()];
        for (const { key } of QUESTION_TOPICS) {
            if (topics.has(key) && typeof facts[key] === "function" && key !== "beds" && key !== "baths" && key !== "sqft") {
                bullets.push(...facts[key]());
            }
        }
    } else {
        // Default set — unchanged from before.
        bullets = [
            ...facts.beds(), ...facts.baths(), ...facts.sqft(),
            ...facts.balcony().slice(0, 1), ...facts.floor(),
            ...(snap && snap.orientation ? [t("units.orientationBullet", { v: snap.orientation })] : []),
            ...facts.price(),
        ];
    }
    // Overlapping topics (view + orientation) can duplicate a fact — dedupe.
    return [...new Set(bullets)];
}

/**
 * RichComponentMessage
 * ---------------------------------------------------------------------------
 * Renders an AWAC (Adaptive Web Agent Component) envelope message.
 *
 * Flow:
 *  1. The agent sends a StaticContentMessage (formatType=Text) whose text is an
 *     AWAC JSON envelope (see helpers/conversationEntryUtil#getAwacEnvelope).
 *  2. conversationEntry.js detects it via isAwacMessage() and renders THIS
 *     component instead of a plain text bubble.
 *  3. This component:
 *      - lifts the envelope up to the parent so it can render the adaptive
 *        component in a SEPARATE gallery panel (onAwacRender(envelope)),
 *      - renders the agent's `text` line (if present) inside the normal chat
 *        bubble (the chat stream itself is left untouched — no inline gallery),
 *      - renders `options` as round-trip action buttons that send a normal
 *        text message back to the agent (onAwacAction), keeping the agent in
 *        the loop.
 *
 * The heavy adaptive component (FloorPlanShowcase) is NOT rendered inline here;
 * it is mounted by GalleryPanel as a separate docked section.
 *
 * @param {object} props
 * @param {object} props.conversationEntry
 * @param {Function} props.onAwacAction - (text) => Promise. Sends a reply to the agent.
 * @param {Function} props.onAwacRender - (envelope, templateName) => void. Opens the gallery panel.
 */
/** Phone inline plan viewer: the SAME lazy UnitCarousel the gallery mounts,
 *  rendered inside the chat thread, starting at the tapped home. */
function InlinePlanCarousel({ envelope, initialIndex, onAwacAction }) {
    const Template = getAwacTemplate("UnitCarousel");
    if (!Template) {
        return null;
    }
    return (
        <div className="awacInlinePlan">
            <Suspense fallback={<div className="awacInlinePlanLoading" aria-hidden="true" />}>
                <Template
                    key={`inline-${initialIndex}`}
                    payload={envelope}
                    onAction={onAwacAction}
                    initialIndex={initialIndex} />
            </Suspense>
        </div>
    );
}

export default function RichComponentMessage({ conversationEntry, onAwacAction, onAwacRender }) {
    const envelope = ConversationEntryUtil.getAwacEnvelope(conversationEntry);
    const templateName = ConversationEntryUtil.getAwacTemplateName(conversationEntry);

    // Track whether the gallery wing is open and which template it shows
    // (broadcast by conversation.js). The "View matching units →" re-open link
    // only renders while the gallery is CLOSED — when the carousel is already
    // on screen the link is noise. Subscribed BEFORE the render effect below
    // so the auto-open dispatch is never missed.
    const [galleryState, setGalleryState] = useState({ open: false, template: null });
    useEffect(() => {
        const onGalleryState = (e) => {
            const d = e && e.detail;
            setGalleryState({ open: !!(d && d.open), template: (d && d.template) || null });
        };
        window.addEventListener("awac:gallery-state", onGalleryState);
        return () => window.removeEventListener("awac:gallery-state", onGalleryState);
    }, []);

    // Phones: unit results don't auto-open the gallery sheet. Instead each
    // unit block carries a "View Plan" button that mounts the carousel INLINE
    // in this message, starting at that home (null = collapsed).
    const [inlinePlan, setInlinePlan] = useState(null);

    // Lift the envelope to the parent so the gallery renders in its own panel.
    useEffect(() => {
        if (envelope && typeof onAwacRender === "function") {
            onAwacRender(envelope, templateName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationEntry.messageId]);

    if (!envelope) {
        return null;
    }

    const introText = typeof envelope.text === "string" ? localizeAgentText(envelope.text, false) : "";
    const options = Array.isArray(envelope.options) ? envelope.options : [];

    // Units discussed => ALWAYS give a bullet summary in the chat bubble (the
    // gallery carries the photos; the summary carries the facts).
    const summaryUnits = templateName === "UnitCarousel" ? extractCarouselUnits(envelope) : [];

    // Template-aware label for the re-open link (the gallery auto-opens on
    // arrival; this link lets the prospect bring it back after closing it).
    // Labels live in the i18n tables (link.<TemplateName>) so they follow the
    // site language.
    const KNOWN_LINK_TEMPLATES = ["UnitCarousel", "NeighborhoodMap", "FloorPlanShowcase", "PoiMapControl"];
    const galleryLinkLabel = KNOWN_LINK_TEMPLATES.indexOf(templateName) !== -1
        ? t(`link.${templateName}`)
        : t("link.gallery");

    function handleOptionClick(option) {
        if (typeof onAwacAction !== "function") {
            return;
        }
        // The text we send back: prefer an explicit `send` string, else the
        // option label/name — LOCALIZED, so the FR site sends exactly what
        // the button showed. Sending the raw English made the outgoing bubble
        // English AND flipped the agent's language detection (its canned
        // confirmations key on the utterance language), cascading a whole
        // English exchange into a French conversation.
        const raw = option.send || option.value || option.name || option.label || "";
        if (!raw) {
            return;
        }
        onAwacAction(localizeOptionLabel(raw));
    }

    // STATE-AWARE OPTIONS: while the unit carousel is open, every visible card
    // carries its own unit-specific "Book a visit" button — the generic chat
    // chip is redundant, so it hides (and returns the moment the gallery
    // closes). Matched on the raw Apex option strings, which are English
    // regardless of the display language.
    const isBookVisitOption = (opt) =>
        /book a visit/i.test(opt.name || "") || /book a visit/i.test(opt.send || "");
    const carouselOnScreen = galleryState.open
        && galleryState.template === "UnitCarousel"
        && templateName === "UnitCarousel";
    const visibleOptions = carouselOnScreen
        ? options.filter((opt) => !isBookVisitOption(opt))
        : options;

    return (
        <div className="awacMessageContainer">
            {introText ? <p className="awacIntroText">{introText}</p> : null}

            {summaryUnits.length > 0 && (
                <div className="awacUnitSummary">
                    {summaryUnits.slice(0, UNIT_SUMMARY_MAX).map((u, i) => {
                        const snap = findUnit(u);
                        const premium = unitFeatureKeys(u).map((k) => t(k));
                        // The gold premium line already names these features —
                        // a bullet repeating one (e.g. "Corner unit") is noise.
                        const bullets = unitFeatureBullets(u, snap, conversationEntry.answeredQuestion)
                            .filter((b) => !premium.includes(b));
                        // Full/partial match against the visitor's query, as
                        // scored by the Apex action; missing lists what a
                        // partial-match unit lacks; notes carry the POSITIVE
                        // framings (budget stretch, extra bedroom, den-as-office).
                        const missing = u.match === "partial" && Array.isArray(u.missing)
                            ? [...new Set(u.missing.map(missingFeatureLabel).filter(Boolean))]
                            : [];
                        const notes = Array.isArray(u.notes)
                            ? [...new Set(u.notes.map(noteLabel).filter(Boolean))]
                            : [];
                        return (
                            <div className="awacUnitBlock" key={u.id || u.name || i}>
                                <strong className="awacUnitBlockName">
                                    {u.name || u.unitNumber || u.ssot__Name__c || `#${i + 1}`}
                                    {u.match === "full" && (
                                        <span className="awacMatchBadge awacMatchFull">
                                            <span aria-hidden="true">✓ </span>{t("match.full")}
                                        </span>
                                    )}
                                    {u.match === "partial" && (
                                        <span className="awacMatchBadge awacMatchPartial">
                                            {t("match.partial")}
                                        </span>
                                    )}
                                </strong>
                                {premium.length > 0 && (
                                    <p className="awacUnitBlockPremium">
                                        <span aria-hidden="true">✦ </span>{premium.join(" · ")}
                                    </p>
                                )}
                                {(bullets.length > 0 || missing.length > 0 || notes.length > 0) && (
                                    <ul className="awacUnitBlockFeatures">
                                        {bullets.map((b, j) => <li key={j}>{b}</li>)}
                                        {notes.map((n, j) => (
                                            <li key={`note-${j}`} className="awacNoteFeat">
                                                <span aria-hidden="true">＋ </span>{n}
                                            </li>
                                        ))}
                                        {missing.map((m, j) => (
                                            <li key={`miss-${j}`} className="awacMissingFeat">
                                                <span aria-hidden="true">✗ </span>{m}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {/* Phone-only (CSS-gated): mount the plan
                                    carousel inline at this home. */}
                                {templateName === "UnitCarousel" && (
                                    <button
                                        type="button"
                                        className="awacViewPlanBtn"
                                        aria-expanded={inlinePlan === i}
                                        onClick={() => setInlinePlan(inlinePlan === i ? null : i)}>
                                        {inlinePlan === i ? t("units.hidePlan") : t("units.viewPlan")}
                                    </button>
                                )}
                                {inlinePlan === i && templateName === "UnitCarousel" && (
                                    <InlinePlanCarousel
                                        envelope={envelope}
                                        initialIndex={i}
                                        onAwacAction={onAwacAction} />
                                )}
                            </div>
                        );
                    })}
                    {summaryUnits.length > UNIT_SUMMARY_MAX && (
                        <p className="awacUnitSummaryMore">
                            {t("unitsSummary.more", { count: summaryUnits.length - UNIT_SUMMARY_MAX })}
                        </p>
                    )}
                </div>
            )}

            {/* Re-open link: only while the gallery is closed (or showing a
                DIFFERENT template) — when this row's content is already on
                screen the link is redundant. */}
            {!(galleryState.open && galleryState.template === templateName) && (
                <button
                    type="button"
                    className={"awacOpenGalleryLink"
                        + (templateName === "UnitCarousel" ? " awacOpenGalleryLinkCarousel" : "")}
                    onClick={() => onAwacRender && onAwacRender(envelope, templateName)}>
                    {galleryLinkLabel} &rarr;
                </button>
            )}

            {(visibleOptions.length || summaryUnits.length > 1 || templateName === "UnitCarousel") ? (
                <div className="awacOptions">
                    {/* Book a visit lives HERE rather than inside the unit card:
                        the card gave that space to the photo caption + thumbnail
                        strip. Routes through the carousel's awac:book-current-unit
                        listener so the booking always names the home currently on
                        screen. Deferred a tick because the gallery may only be
                        mounting on this very click. */}
                    {templateName === "UnitCarousel" && (
                        <button
                            type="button"
                            className="awacOptionButton awacClientChip"
                            onClick={() => {
                                if (typeof onAwacRender === "function") {
                                    onAwacRender(envelope, templateName); // ensure the gallery is open
                                }
                                setTimeout(
                                    () => window.dispatchEvent(new CustomEvent("awac:book-current-unit")),
                                    0
                                );
                            }}>
                            {t("units.book")}
                        </button>
                    )}
                    {/* Client-side quick action: advances the gallery's one-home-
                        at-a-time pager via the awac:next-unit window event — no
                        message is sent to the agent. Rendered only when several
                        homes are on display. */}
                    {summaryUnits.length > 1 && (
                        <button
                            type="button"
                            className="awacOptionButton awacClientChip"
                            onClick={() => {
                                if (typeof onAwacRender === "function") {
                                    onAwacRender(envelope, templateName); // ensure the gallery is open
                                }
                                window.dispatchEvent(new CustomEvent("awac:next-unit"));
                            }}>
                            {t("units.nextUnitChip")}
                        </button>
                    )}
                    {visibleOptions.map((opt, i) => (
                        <button
                            key={`${util.generateUUID()}-${i}`}
                            type="button"
                            className="awacOptionButton"
                            onClick={() => handleOptionClick(opt)}>
                            {localizeOptionLabel(opt.name || opt.label || opt.send || "Select")}
                        </button>
                    ))}
                </div>
            ) : null}

            {conversationEntry.responseTimeMs != null && (
                <p className="awacResponseTime">
                    ⏱ {(conversationEntry.responseTimeMs / 1000).toFixed(1)}s
                </p>
            )}
        </div>
    );
}
